import React, { useCallback } from 'react';
import FoldersSection from './components/FoldersSection';
import UnfiledSection from './components/UnfiledSection';
import { useDocsSection } from './hooks';

/**
 * Docs area body: folder tree (no wrapper), then Unfiled inbox.
 * Organization persists via useDocOrganization; drop on a folder to file,
 * or on Unfiled to take a doc / folder back to the root.
 */
const DocsSection = ({
  docs = [],
  loading = false,
  selectedDocId = null,
  onSelectDoc,
  onOpenInNewTab,
  onCreateDoc,
  onDeleteDoc,
  createDoc,
}) => {
  const {
    organization,
    ready,
    folders,
    rootFolders,
    unfiledDocs,
    docsByFolder,
    unfiledExpanded,
    setUnfiledExpanded,
    collapsedFolders,
    creatingFolder,
    creatingUnderParentId,
    newFolderName,
    setNewFolderName,
    dropTargetId,
    dropHint,
    draggedFolderId,
    newFolderInputRef,
    handleToggleFolderExpand,
    handleStartCreateFolder,
    handleCommitNewFolder,
    handleNewFolderKeyDown,
    handleDragOverTarget,
    handleDragLeaveTarget,
    handleFolderDragStart,
    handleFolderDragEnd,
    handleDropOnFolder,
    handleDropOnUnfiled,
    handleCollapseAll,
    canCollapseAll,
  } = useDocsSection({ docs, selectedDocId });

  const handleCreateDoc = useCallback(() => {
    setUnfiledExpanded(true);
    onCreateDoc?.();
  }, [onCreateDoc, setUnfiledExpanded]);

  return (
    <div className="sidebar__docs-section sidebar__pane-scroll">
      <FoldersSection
        ready={ready}
        folders={folders}
        rootFolders={rootFolders}
        creatingFolder={creatingFolder}
        creatingUnderParentId={creatingUnderParentId}
        newFolderName={newFolderName}
        onNewFolderNameChange={setNewFolderName}
        newFolderInputRef={newFolderInputRef}
        onStartCreateFolder={handleStartCreateFolder}
        onCreateDoc={handleCreateDoc}
        onCollapseAll={handleCollapseAll}
        canCollapseAll={canCollapseAll}
        onCommitNewFolder={handleCommitNewFolder}
        onNewFolderKeyDown={handleNewFolderKeyDown}
        createDoc={createDoc}
        docsByFolder={docsByFolder}
        collapsedFolders={collapsedFolders}
        onToggleFolderExpand={handleToggleFolderExpand}
        selectedDocId={selectedDocId}
        onSelectDoc={onSelectDoc}
        onOpenInNewTab={onOpenInNewTab}
        onDeleteDoc={onDeleteDoc}
        organization={organization}
        dropTargetId={dropTargetId}
        dropHint={dropHint}
        draggedFolderId={draggedFolderId}
        onDragOverTarget={handleDragOverTarget}
        onDropOnFolder={handleDropOnFolder}
        onDragLeaveTarget={handleDragLeaveTarget}
        onFolderDragStart={handleFolderDragStart}
        onFolderDragEnd={handleFolderDragEnd}
      />

      <UnfiledSection
        loading={loading}
        unfiledDocs={unfiledDocs}
        unfiledExpanded={unfiledExpanded}
        onToggleUnfiled={() => setUnfiledExpanded((v) => !v)}
        selectedDocId={selectedDocId}
        onSelectDoc={onSelectDoc}
        onOpenInNewTab={onOpenInNewTab}
        onDeleteDoc={onDeleteDoc}
        organization={organization}
        dropTargetId={dropTargetId}
        onDragOverTarget={handleDragOverTarget}
        onDragLeaveTarget={handleDragLeaveTarget}
        onDropOnUnfiled={handleDropOnUnfiled}
        createDoc={createDoc}
      />
    </div>
  );
};

export default DocsSection;
